/**
 * LeadsView - Full-screen view for leads management
 * Matches the visual style of the Library view
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { TabGroup, TabItem } from "@/components/ui/TabGroup";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { UploadIcon, DownloadIcon } from "@/components/icons";
import { EntityCreateButton } from "@/core/entityConfig";
import { LeadsTable } from "./components/LeadsTable";
import { LeadDetailModal } from "./components/LeadDetailModal";
import { LeadConvertModal } from "./components/LeadConvertModal";
import { LeadImportModal } from "./components/LeadImportModal";
import { AddLeadModal } from "./components/AddLeadModal";
import { OutreachEmailModal } from "./components/OutreachEmailModal";
import { EmailComposeModal } from "@/components/email/EmailComposeModal";
import { BulkActionsDropdown } from "./components/BulkActionsDropdown";
import { LeadStatsBar } from "./components/LeadStatsBar";
import { useLeadStats } from "./hooks/useLeadStats";
import { useQueryClient } from "@tanstack/react-query";
import {
  leadKeys,
  useLeads,
  useLeadsFiltered,
  useUpdateLeadStatus,
  useDeleteLead,
  useBulkDeleteLeads,
} from "./hooks/useLeads";
import { leadsToCSV, downloadCSV, generateExportFilename } from "./utils/csvExport";
import { leadService } from "./Lead.service";
import { useAccountRealtimeSync } from "@/hooks/useAccountRealtimeSync";
import type { LeadStatus, LeadFilterParams } from "./Lead.types";
import { LEAD_STATUS_CONFIG } from "./Lead.types";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";

type StatusFilter = "ALL" | LeadStatus;
type SortField = "score" | "name" | "instagram_followers" | "google_rating" | "created_at";

const VALID_STATUSES: StatusFilter[] = [
  "ALL", "DISCOVERED", "NEW", "RESEARCHING", "READY_TO_CONTACT", "CONTACTED",
  "FOLLOW_UP", "QUALIFIED", "WON", "LOST", "DISQUALIFIED"
];
const VALID_SORT_FIELDS: SortField[] = [
  "score", "name", "instagram_followers", "google_rating", "created_at"
];

export function LeadsView() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL with defaults
  const statusFilter = (VALID_STATUSES.includes(searchParams.get("status") as StatusFilter)
    ? searchParams.get("status")
    : "ALL") as StatusFilter;
  const sectorFilter = searchParams.get("sector") || "";
  const cityFilter = searchParams.get("city") || "";
  const sortBy = (VALID_SORT_FIELDS.includes(searchParams.get("sort") as SortField)
    ? searchParams.get("sort")
    : "score") as SortField;
  const sortOrder = (searchParams.get("order") === "asc" ? "asc" : "desc") as "asc" | "desc";
  const searchQuery = searchParams.get("q") || "";

  // Update URL params helper
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === "" || value === "ALL" || (key === "sort" && value === "score") || (key === "order" && value === "desc")) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Filter setters that update URL
  const setStatusFilter = useCallback((status: StatusFilter) => updateParams({ status }), [updateParams]);
  const setSectorFilter = useCallback((sector: string) => updateParams({ sector }), [updateParams]);
  const setCityFilter = useCallback((city: string) => updateParams({ city }), [updateParams]);

  // Search input with debouncing - local state for immediate feedback, debounced URL update
  const [searchInput, setSearchInput] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        updateParams({ q: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, updateParams]);

  // Sync local input when URL changes externally (e.g., browser back/forward)
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Modal state
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showOutreachModal, setShowOutreachModal] = useState(false);
  const [outreachLeadId, setOutreachLeadId] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLeadId, setEmailLeadId] = useState<string | null>(null);
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);

  // Selection state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Enrichment state
  const [enrichingIds, setEnrichingIds] = useState<string[]>([]);

  // Build query params
  const queryParams: LeadFilterParams = useMemo(() => ({
    sortBy,
    sortOrder,
    status: statusFilter === "ALL" ? undefined : statusFilter,
    sector: sectorFilter || undefined,
    city: cityFilter === "__EMPTY__" ? undefined : (cityFilter || undefined),
    cityEmpty: cityFilter === "__EMPTY__",
    includeConverted: statusFilter === "WON" || statusFilter === "LOST" || statusFilter === "DISQUALIFIED",
  }), [statusFilter, sectorFilter, cityFilter, sortBy, sortOrder]);

  // Queries and mutations
  const { data: leads = [], isLoading } = useLeadsFiltered(queryParams);
  const { data: allLeads = [] } = useLeads(true);
  const leadStats = useLeadStats(allLeads);
  const queryClient = useQueryClient();
  const updateStatusMutation = useUpdateLeadStatus();
  const deleteMutation = useDeleteLead();
  const bulkDeleteMutation = useBulkDeleteLeads();

  // Account-level SSE for lead enrichment events
  const { on: onAccountEvent } = useAccountRealtimeSync();

  useEffect(() => {
    const unsubscribe = onAccountEvent("lead_enriched", (data) => {
      const enrichedIds = (data.leadIds as string[]) || [];
      // Remove enriched leads from loading state
      setEnrichingIds((prev) =>
        prev.filter((id) => !enrichedIds.includes(id))
      );
      // Refetch leads to get updated data
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    });
    return unsubscribe;
  }, [onAccountEvent, queryClient]);

  const handleEnrichLead = useCallback(async (leadId: string) => {
    setEnrichingIds((prev) => [...prev, leadId]);
    try {
      await leadService.enrichLead(leadId);
      // Safety timeout: clear spinner after 60s if SSE event never arrives
      setTimeout(() => {
        setEnrichingIds((prev) => prev.filter((id) => id !== leadId));
        queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      }, 60_000);
    } catch {
      setEnrichingIds((prev) => prev.filter((id) => id !== leadId));
    }
  }, [queryClient]);

  // Get unique sectors, cities, and countries from ALL leads (not just filtered)
  const { sectors, cities, countries } = useMemo(() => {
    const sectorSet = new Set<string>();
    const citySet = new Set<string>();
    const countrySet = new Set<string>();
    allLeads.forEach((lead) => {
      if (lead.sector) sectorSet.add(lead.sector);
      if (lead.city) citySet.add(lead.city);
      if (lead.country) countrySet.add(lead.country);
    });
    return {
      sectors: Array.from(sectorSet).sort(),
      cities: Array.from(citySet).sort(),
      countries: Array.from(countrySet).sort(),
    };
  }, [allLeads]);

  // Filter leads by search query (use searchInput for immediate feedback)
  const filteredLeads = useMemo(() => {
    if (!searchInput.trim()) return leads;
    const query = searchInput.toLowerCase();
    return leads.filter(
      (lead) =>
        lead.name.toLowerCase().includes(query) ||
        lead.sector?.toLowerCase().includes(query) ||
        lead.city?.toLowerCase().includes(query)
    );
  }, [leads, searchInput]);

  // Tab items for status filter - use allLeads for counts so they don't disappear when filtering
  const statusTabs: TabItem<StatusFilter>[] = [
    { id: "ALL", label: "Tutti", badge: allLeads.length },
    ...Object.entries(LEAD_STATUS_CONFIG)
      .filter(([status]) => !["WON", "LOST", "DISQUALIFIED"].includes(status))
      .map(([status, config]) => ({
        id: status as LeadStatus,
        label: config.label,
        badge: allLeads.filter((l) => l.status === status).length,
      })),
    { id: "WON", label: LEAD_STATUS_CONFIG.WON.label, badge: allLeads.filter((l) => l.status === "WON").length },
    { id: "LOST", label: LEAD_STATUS_CONFIG.LOST.label, badge: allLeads.filter((l) => l.status === "LOST").length },
    { id: "DISQUALIFIED", label: LEAD_STATUS_CONFIG.DISQUALIFIED.label, badge: allLeads.filter((l) => l.status === "DISQUALIFIED").length },
  ];

  const handleViewLead = (leadId: string) => {
    setSelectedLeadId(leadId);
    setShowDetailModal(true);
  };

  const handleStatusChange = async (leadId: string, status: LeadStatus) => {
    await updateStatusMutation.mutateAsync({ leadId, status });
  };

  const handleAddNote = (leadId: string) => {
    setSelectedLeadId(leadId);
    setShowDetailModal(true);
  };

  const handleDeleteLead = (leadId: string) => {
    setLeadToDelete(leadId);
  };

  const handleGenerateOutreach = (leadId: string) => {
    setOutreachLeadId(leadId);
    setShowOutreachModal(true);
  };

  const handleSendEmail = (leadId: string) => {
    setEmailLeadId(leadId);
    setShowEmailModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!leadToDelete) return;
    await deleteMutation.mutateAsync(leadToDelete);
    setLeadToDelete(null);
  };

  const handleConvert = (leadId: string) => {
    setSelectedLeadId(leadId);
    setShowDetailModal(false);
    setShowConvertModal(true);
  };

  const handleConvertComplete = () => {
    setShowConvertModal(false);
    setSelectedLeadId(null);
  };

  // Selection handlers
  const handleToggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const visibleIds = filteredLeads.map((lead) => lead.id);
    const allSelected = visibleIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      // Deselect all visible
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      // Select all visible (merge without duplicates)
      setSelectedIds((prev) => {
        const combined = [...prev];
        visibleIds.forEach((id) => {
          if (!combined.includes(id)) {
            combined.push(id);
          }
        });
        return combined;
      });
    }
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedIds([]);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.length === 0) return;
    await bulkDeleteMutation.mutateAsync(selectedIds);
    setShowBulkDeleteConfirm(false);
    setSelectedIds([]);
    setIsSelectionMode(false);
  };

  const handleBulkComplete = () => {
    setSelectedIds([]);
    setIsSelectionMode(false);
  };

  const emailLead = emailLeadId
    ? filteredLeads.find((l) => l.id === emailLeadId) ??
      allLeads.find((l) => l.id === emailLeadId) ??
      null
    : null;

  return (
    <PageLayout>
      <PageHeader>
        <PageHeader.Row>
          <TabGroup
            items={statusTabs}
            activeId={statusFilter}
            onTabChange={setStatusFilter}
            config={{
              variant: "navigation",
              size: "sm",
              layout: "horizontal",
              hasBackground: true,
            }}
          />
          <PageHeader.Actions>
            <EntityCreateButton
              entity="lead"
              onClick={() => setShowAddModal(true)}
              variant="secondary"
            />
            <button
              onClick={() => {
                const csv = leadsToCSV(filteredLeads);
                const filename = generateExportFilename(statusFilter);
                downloadCSV(csv, filename);
              }}
              disabled={filteredLeads.length === 0}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DownloadIcon className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <UploadIcon className="w-4 h-4" />
              Import CSV
            </button>
          </PageHeader.Actions>
        </PageHeader.Row>
      </PageHeader>

      <div className="px-4 lg:px-6 space-y-4 shrink-0">
        <LeadStatsBar stats={leadStats} />

        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 shrink-0">
                {/* Sector filter */}
                <select
                  value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                  className="w-40 text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white"
                >
                  <option value="">All Sectors</option>
                  {sectors.map((sector) => (
                    <option key={sector} value={sector}>
                      {sector}
                    </option>
                  ))}
                </select>

                {/* City filter */}
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="w-36 text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white"
                >
                  <option value="">All Cities</option>
                  <option value="__EMPTY__">No City</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>

                {/* Sort dropdown */}
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split("-") as [SortField, "asc" | "desc"];
                    updateParams({ sort: field, order });
                  }}
                  className="w-48 text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white"
                >
                  <option value="score-desc">Score (High to Low)</option>
                  <option value="score-asc">Score (Low to High)</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="instagram_followers-desc">IG Followers (High to Low)</option>
                  <option value="google_rating-desc">Google Rating (High to Low)</option>
                  <option value="created_at-desc">Newest First</option>
                  <option value="created_at-asc">Oldest First</option>
                </select>
              </div>

              {/* Search */}
              <div className="flex-1 max-w-xs ml-4">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
      </div>

      <div className="px-4 lg:px-6 shrink-0">
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!isSelectionMode ? (
                <button
                  onClick={() => setIsSelectionMode(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Select
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancelSelection}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel
                  </button>
                  <span className="text-sm text-gray-600">
                    {selectedIds.length} selected
                  </span>
                  {selectedIds.length > 0 && (
                    <button
                      onClick={() => setSelectedIds([])}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Deselect All
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isSelectionMode && selectedIds.length > 0 && (
                <>
                  <BulkActionsDropdown
                    selectedIds={selectedIds}
                    cities={cities}
                    sectors={sectors}
                    countries={countries}
                    onComplete={handleBulkComplete}
                  />
                  <button
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete ({selectedIds.length})
                  </button>
                </>
              )}
              {!isSelectionMode && (
                <button
                  onClick={() => {
                    setIsSelectionMode(true);
                    setSelectedIds(filteredLeads.map((lead) => lead.id));
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Select All
                </button>
              )}
            </div>
          </div>
      </div>

      <PageSection.Card>
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <LeadsTable
            leads={filteredLeads}
            onViewLead={handleViewLead}
            onStatusChange={handleStatusChange}
            onAddNote={handleAddNote}
            onDeleteLead={handleDeleteLead}
            onEnrichLead={handleEnrichLead}
            onGenerateOutreach={handleGenerateOutreach}
            onSendEmail={handleSendEmail}
            enrichingIds={enrichingIds}
            isUpdating={updateStatusMutation.isPending}
            selectionMode={{
              isActive: isSelectionMode,
              selectedIds,
              onToggle: handleToggleSelection,
              onSelectAll: handleSelectAll,
            }}
          />
        )}
      </PageSection.Card>

      {/* Modals */}
      <LeadDetailModal
        leadId={selectedLeadId}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedLeadId(null);
        }}
        onConvert={handleConvert}
      />

      <LeadConvertModal
        leadId={selectedLeadId}
        isOpen={showConvertModal}
        onClose={() => {
          setShowConvertModal(false);
          setSelectedLeadId(null);
        }}
        onConvertComplete={handleConvertComplete}
      />

      <ConfirmDialog
        isOpen={!!leadToDelete}
        onClose={() => setLeadToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDeleteConfirm}
        title="Delete Selected Leads"
        message={`Delete ${selectedIds.length} lead${selectedIds.length > 1 ? 's' : ''}? This cannot be undone.`}
        confirmText="Delete All"
        variant="danger"
        isLoading={bulkDeleteMutation.isPending}
      />

      <LeadImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
      />

      <AddLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      <OutreachEmailModal
        isOpen={showOutreachModal}
        onClose={() => setShowOutreachModal(false)}
        lead={
          outreachLeadId
            ? filteredLeads.find((l) => l.id === outreachLeadId) ??
              allLeads.find((l) => l.id === outreachLeadId) ??
              null
            : null
        }
        onEmailGenerated={() => {
          queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
        }}
      />

      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setEmailLeadId(null);
        }}
        recipientEmail={emailLead?.emailAddress || ""}
        recipientName={emailLead?.name || ""}
        leadId={emailLeadId || ""}
        initialSubject={emailLead?.outreachEmailData ? "" : ""}
        initialBody={emailLead?.outreachEmailData?.email || ""}
        onEmailSent={() => {
          queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
        }}
      />

    </PageLayout>
  );
}
